import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shop } from '@/api/shop';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import type { OrderRow, OrderStatus, OrderItemRow } from '@/api/shop';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Receipt, Eye } from 'lucide-react';
import { cn } from '@/lib/cn';

const STATUS_FILTERS: Array<{ value: OrderStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'cart', label: 'Cart' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'paid', label: 'Paid' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

export function OrdersList() {
  const effectiveCustomerId = useEffectiveCustomerId();
  const [status, setStatus] = useState<OrderStatus | 'all'>('all');
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', status, effectiveCustomerId],
    queryFn: () => shop.listOrders({
      status: status === 'all' ? undefined : status,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  // Compute the total $ for the current view
  const totalCents = useMemo(
    () => rows.reduce((sum, r) => sum + r.total_cents, 0),
    [rows],
  );

  return (
    <>
      <PageHeader
        eyebrow="Shop"
        title="Orders"
        description="Voice-checkout orders. Every cart → submitted → paid transition lands here."
      />

      <div className="mb-4 flex items-center gap-3">
        <Select value={status} onValueChange={(v) => setStatus(v as OrderStatus | 'all')}>
          <SelectTrigger className="w-48 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">
          <span className="font-mono tabular-nums">{rows.length}</span> orders ·{' '}
          <span className="font-mono tabular-nums font-semibold text-foreground">
            ${(totalCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>{' '}
          total
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-0.5">
          {rows.map((r) => (
            <ReceiptRow key={r.id} row={r} onInspect={() => setDetailId(r.id)} />
          ))}
        </div>
      )}

      {detailId && (
        <OrderDetailDialog
          orderId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <Receipt className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No orders yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          When callers reach a shopping IVR node, a cart lands here — then flips
          to <span className="font-mono">submitted</span> and (after a payment node) to{' '}
          <span className="font-mono">paid</span>.
        </p>
      </CardContent>
    </Card>
  );
}

const STATUS_STAMP: Record<OrderStatus, { label: string; className: string }> = {
  cart:       { label: 'CART',       className: 'bg-slate-100 text-slate-600 border-slate-300' },
  submitted:  { label: 'SUBMITTED',  className: 'bg-sky-50 text-sky-800 border-sky-400' },
  paid:       { label: 'PAID',       className: 'bg-emerald-50 text-emerald-800 border-emerald-500' },
  shipped:    { label: 'SHIPPED',    className: 'bg-indigo-50 text-indigo-800 border-indigo-500' },
  cancelled:  { label: 'CANCELLED',  className: 'bg-rose-50 text-rose-800 border-rose-400 line-through' },
  refunded:   { label: 'REFUNDED',   className: 'bg-amber-50 text-amber-800 border-amber-500' },
};

function ReceiptRow({
  row, onInspect,
}: { row: OrderRow; onInspect: () => void }) {
  const stamp = STATUS_STAMP[row.status];
  return (
    <button
      type="button"
      onClick={onInspect}
      className="group w-full text-left grid grid-cols-[9rem_7rem_minmax(0,1fr)_7rem_8rem_2rem] items-center gap-3 px-4 py-3 rounded-sm border-y border-dashed border-slate-200 bg-white hover:bg-slate-50 transition-colors font-mono text-xs"
    >
      <span className="font-bold tracking-tight text-sm text-slate-900">
        #{row.number}
      </span>

      <span className={cn(
        'inline-flex items-center justify-center px-2 py-0.5 border-2 rounded text-[10px] font-bold tracking-[0.15em] w-fit',
        stamp.className,
      )}>
        {stamp.label}
      </span>

      <span className="text-xs text-muted-foreground truncate">
        <span className="tabular-nums">{row.item_count}</span>{' '}
        item{row.item_count === 1 ? '' : 's'}
        {row.caller_id && (
          <> · caller {row.caller_id.slice(0, 8)}…</>
        )}
      </span>

      <time
        dateTime={row.created_at}
        className="text-[11px] text-muted-foreground tabular-nums text-right"
      >
        {new Date(row.created_at).toLocaleDateString(undefined, {
          month: 'short', day: 'numeric', year: 'numeric',
        })}
      </time>

      <span className="text-base font-bold tabular-nums text-right">
        ${(row.total_cents / 100).toFixed(2)}
      </span>

      <Eye className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-600 transition-colors justify-self-end" />
    </button>
  );
}

function OrderDetailDialog({
  orderId, onClose,
}: { orderId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => shop.getOrder(orderId),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg font-mono">
        <DialogHeader>
          <DialogTitle>
            {isLoading ? 'Loading…' : data ? `Order #${data.order.number}` : 'Order'}
          </DialogTitle>
        </DialogHeader>
        {data && (
          <div className="py-2">
            <ReceiptView order={data.order} items={data.items} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReceiptView({ order, items }: { order: OrderRow; items: OrderItemRow[] }) {
  const stamp = STATUS_STAMP[order.status];
  return (
    <div className="rounded border-2 border-slate-200 bg-slate-50 p-4 text-xs">
      <div className="text-center pb-3 border-b border-dashed border-slate-400">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Ivrello receipt</div>
        <div className="mt-1 text-base font-bold tracking-tight">#{order.number}</div>
        <div className={cn(
          'inline-flex mt-2 px-3 py-1 border-2 rounded text-[10px] font-bold tracking-[0.15em]',
          stamp.className,
        )}>
          {stamp.label}
        </div>
      </div>

      <div className="py-3 border-b border-dashed border-slate-400 space-y-1.5">
        {items.map((it) => (
          <div key={it.id} className="flex items-start gap-2">
            <span className="tabular-nums text-slate-500 w-8 shrink-0 text-right">
              {it.quantity}×
            </span>
            <span className="flex-1 truncate">{it.name}</span>
            <span className="tabular-nums">
              ${(it.amount_cents / 100).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      <div className="py-3 border-b border-dashed border-slate-400 space-y-1">
        <SubtotalLine label="Subtotal" cents={order.subtotal_cents} />
        {order.tax_cents > 0 && <SubtotalLine label="Tax" cents={order.tax_cents} />}
        {order.shipping_cents > 0 && <SubtotalLine label="Shipping" cents={order.shipping_cents} />}
      </div>

      <div className="pt-3 flex items-center justify-between text-sm font-bold">
        <span className="uppercase tracking-wider">Total</span>
        <span className="tabular-nums">${(order.total_cents / 100).toFixed(2)}</span>
      </div>

      <div className="mt-4 pt-3 border-t border-dashed border-slate-400 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-slate-500">
        <span className="uppercase tracking-widest">Created</span>
        <span className="tabular-nums text-right">{new Date(order.created_at).toLocaleString()}</span>
        {order.placed_at && (
          <>
            <span className="uppercase tracking-widest">Placed</span>
            <span className="tabular-nums text-right">{new Date(order.placed_at).toLocaleString()}</span>
          </>
        )}
        {order.paid_at && (
          <>
            <span className="uppercase tracking-widest">Paid</span>
            <span className="tabular-nums text-right">{new Date(order.paid_at).toLocaleString()}</span>
          </>
        )}
        {order.shipped_at && (
          <>
            <span className="uppercase tracking-widest">Shipped</span>
            <span className="tabular-nums text-right">{new Date(order.shipped_at).toLocaleString()}</span>
          </>
        )}
        {order.caller_id && (
          <>
            <span className="uppercase tracking-widest">Caller</span>
            <span className="tabular-nums text-right truncate">{order.caller_id}</span>
          </>
        )}
      </div>

      <div className="mt-4 text-center text-[10px] uppercase tracking-[0.3em] text-slate-400">
        — thank you —
      </div>
    </div>
  );
}

function SubtotalLine({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="tabular-nums">${(cents / 100).toFixed(2)}</span>
    </div>
  );
}
